import type { RulesPiece } from '@/game/rules';

export const CAPTURE_PHASE_ORDER = [
  'anticipation',
  'travel',
  'contact',
  'follow-through',
  'hold',
  'settle',
] as const;

export type CapturePhaseName = typeof CAPTURE_PHASE_ORDER[number];
export type ScreenCell = readonly [column: number, row: number];

export interface ScreenDirection {
  readonly deltaX: number;
  readonly deltaY: number;
  readonly unitX: number;
  readonly unitY: number;
  readonly perpendicularX: number;
  readonly perpendicularY: number;
}

interface MotionPose {
  readonly progress: number;
  readonly along: number;
  readonly side: number;
  readonly lift: number;
  readonly rotate: number;
  readonly scaleX: number;
  readonly scaleY: number;
  readonly shadow: number;
  readonly opacity?: number;
}

interface TargetRecoil {
  readonly distance: number;
  readonly side: number;
  readonly lift: number;
  readonly rotate: number;
  readonly scale: number;
}

interface ImpactMotion {
  readonly rotate: number;
  readonly scale: number;
  readonly drift: number;
}

export interface CaptureActionSpec {
  readonly archetype: string;
  readonly impactMark: string;
  readonly accentClass: string;
  readonly reactionFamily: string;
  readonly effectFamily: string;
  readonly effectLayers: readonly CaptureEffectLayer[];
  readonly timings: Readonly<Record<CapturePhaseName, number>>;
  readonly poses: Readonly<Record<CapturePhaseName, MotionPose>>;
  readonly targetRecoil: TargetRecoil;
  readonly impact: ImpactMotion;
}

export interface CaptureEffectLayer {
  readonly plane: 'back' | 'ground' | 'front';
  readonly className: string;
  readonly keyframes: readonly Keyframe[];
  readonly delayMs: number;
  readonly durationMs: number;
}

function effectLayer(
  plane: CaptureEffectLayer['plane'],
  className: string,
  delayMs: number,
  durationMs: number,
  keyframes: readonly Keyframe[],
): CaptureEffectLayer {
  return Object.freeze({ plane, className, delayMs, durationMs, keyframes: Object.freeze(keyframes) });
}

const REST_POSE: MotionPose = Object.freeze({
  progress: 1,
  along: 0,
  side: 0,
  lift: 0,
  rotate: 0,
  scaleX: 1,
  scaleY: 1,
  shadow: 2,
});

export const CAPTURE_ACTION_REGISTRY = {
  p: {
    archetype: 'pawn-jab',
    impactMark: 'reed-jab',
    accentClass: 'is-pawn-jab',
    reactionFamily: 'compact-chip-recoil',
    effectFamily: 'branch-jab-chips',
    effectLayers: [
      effectLayer('ground', 'is-pawn-chips', 0, 110, [{ opacity: 0, transform: 'translate(0%,8%) scale(0.3)' }, { opacity: 0.95, transform: 'translate(-8%,-4%) scale(1)', offset: 0.3 }, { opacity: 0, transform: 'translate(-22%,-18%) scale(1.3)' }]),
      effectLayer('front', 'is-pawn-branch', 6, 104, [{ opacity: 0, transform: 'translate(-34%,10%) rotate(-12deg) scale(0.45,0.7)' }, { opacity: 1, transform: 'translate(4%,-2%) rotate(4deg) scale(1.2,1)', offset: 0.34 }, { opacity: 0, transform: 'translate(28%,-8%) rotate(10deg) scale(1.35,0.72)' }]),
    ],
    timings: { anticipation: 170, travel: 220, contact: 130, 'follow-through': 160, hold: 160, settle: 210 },
    poses: {
      anticipation: { progress: 0, along: -14, side: 0, lift: 2, rotate: -7, scaleX: 0.88, scaleY: 1.08, shadow: 3 },
      travel: { progress: 0.7, along: -3, side: 0, lift: 22, rotate: 4, scaleX: 1.08, scaleY: 0.94, shadow: 11 },
      contact: { progress: 1, along: 10, side: 0, lift: 1, rotate: 8, scaleX: 1.18, scaleY: 0.9, shadow: 5 },
      'follow-through': { progress: 1, along: 16, side: 1, lift: 5, rotate: 5, scaleX: 1.08, scaleY: 0.95, shadow: 8 },
      hold: { progress: 1, along: 8, side: 0, lift: 1, rotate: 1, scaleX: 1.03, scaleY: 0.98, shadow: 4 },
      settle: REST_POSE,
    },
    targetRecoil: { distance: 28, side: 3, lift: 10, rotate: 8, scale: 0.84 },
    impact: { rotate: 12, scale: 1.34, drift: 9 },
  },
  n: {
    archetype: 'knight-kick',
    impactMark: 'leaf-hoof-arc',
    accentClass: 'is-knight-kick',
    reactionFamily: 'lateral-tumble',
    effectFamily: 'hoof-wind-ribbon',
    effectLayers: [
      effectLayer('back', 'is-knight-wind', 0, 118, [{ opacity: 0, transform: 'translate(-38%,18%) rotate(-24deg) scale(0.45,0.7)' }, { opacity: 0.82, transform: 'translate(-4%,-10%) rotate(-8deg) scale(1.15,1)', offset: 0.42 }, { opacity: 0, transform: 'translate(36%,-28%) rotate(14deg) scale(1.55,0.72)' }]),
      effectLayer('front', 'is-knight-hoof', 18, 108, [{ opacity: 0, transform: 'translate(-12%,18%) rotate(-18deg) scale(0.35)' }, { opacity: 1, transform: 'translate(8%,-8%) rotate(12deg) scale(1.08)', offset: 0.28 }, { opacity: 0, transform: 'translate(30%,-24%) rotate(34deg) scale(1.42)' }]),
    ],
    timings: { anticipation: 190, travel: 250, contact: 130, 'follow-through': 180, hold: 150, settle: 200 },
    poses: {
      anticipation: { progress: 0, along: -8, side: -9, lift: 3, rotate: 13, scaleX: 0.94, scaleY: 0.86, shadow: 4 },
      travel: { progress: 0.64, along: 0, side: 18, lift: 39, rotate: -15, scaleX: 0.96, scaleY: 1.18, shadow: 18 },
      contact: { progress: 1, along: 7, side: 6, lift: 12, rotate: -11, scaleX: 1.14, scaleY: 0.94, shadow: 9 },
      'follow-through': { progress: 1, along: 17, side: -8, lift: 18, rotate: 12, scaleX: 1.04, scaleY: 1.08, shadow: 13 },
      hold: { progress: 1, along: 8, side: -4, lift: 8, rotate: 5, scaleX: 1.02, scaleY: 1.04, shadow: 8 },
      settle: REST_POSE,
    },
    targetRecoil: { distance: 34, side: -12, lift: 22, rotate: 15, scale: 0.8 },
    impact: { rotate: -28, scale: 1.5, drift: 14 },
  },
  b: {
    archetype: 'bishop-slash',
    impactMark: 'diagonal-leaf-cut',
    accentClass: 'is-bishop-slash',
    reactionFamily: 'diagonal-cut-rotation',
    effectFamily: 'staff-leaf-ribbon',
    effectLayers: [
      effectLayer('back', 'is-bishop-leaves-back', 0, 108, [{ opacity: 0, transform: 'translate(-26%,24%) rotate(-38deg) scale(0.4)' }, { opacity: 0.72, transform: 'translate(-8%,-6%) rotate(-12deg) scale(1)', offset: 0.38 }, { opacity: 0, transform: 'translate(18%,-30%) rotate(26deg) scale(1.38)' }]),
      effectLayer('front', 'is-bishop-leaves-front', 12, 104, [{ opacity: 0, transform: 'translate(24%,18%) rotate(34deg) scale(0.34)' }, { opacity: 1, transform: 'translate(6%,-10%) rotate(8deg) scale(1.12)', offset: 0.34 }, { opacity: 0, transform: 'translate(-24%,-34%) rotate(-32deg) scale(1.48)' }]),
    ],
    timings: { anticipation: 160, travel: 230, contact: 120, 'follow-through': 190, hold: 160, settle: 190 },
    poses: {
      anticipation: { progress: 0, along: -11, side: -7, lift: 5, rotate: -12, scaleX: 0.91, scaleY: 0.96, shadow: 5 },
      travel: { progress: 0.76, along: 0, side: -13, lift: 19, rotate: 10, scaleX: 1.12, scaleY: 0.97, shadow: 12 },
      contact: { progress: 1, along: 12, side: 9, lift: 6, rotate: 18, scaleX: 1.17, scaleY: 0.92, shadow: 7 },
      'follow-through': { progress: 1, along: 20, side: 14, lift: 3, rotate: 23, scaleX: 1.07, scaleY: 0.96, shadow: 8 },
      hold: { progress: 1, along: 9, side: 5, lift: 2, rotate: 8, scaleX: 1.02, scaleY: 0.99, shadow: 4 },
      settle: REST_POSE,
    },
    targetRecoil: { distance: 31, side: 17, lift: 12, rotate: 20, scale: 0.78 },
    impact: { rotate: 38, scale: 1.58, drift: 16 },
  },
  r: {
    archetype: 'rook-slam',
    impactMark: 'wood-thump',
    accentClass: 'is-rook-slam',
    reactionFamily: 'pancake-compression',
    effectFamily: 'ground-ring-dust',
    effectLayers: [
      effectLayer('ground', 'is-rook-ring', 22, 110, [{ opacity: 0, transform: 'translateY(16%) scale(0.32,0.18)' }, { opacity: 0.9, transform: 'translateY(13%) scale(1.02,0.48)', offset: 0.32 }, { opacity: 0, transform: 'translateY(10%) scale(1.62,0.7)' }]),
      effectLayer('front', 'is-rook-dust', 38, 104, [{ opacity: 0, transform: 'translateY(24%) scale(0.5,0.34)' }, { opacity: 0.88, transform: 'translateY(6%) scale(1.15,0.8)', offset: 0.3 }, { opacity: 0, transform: 'translateY(-22%) scale(1.5,1.2)' }]),
    ],
    timings: { anticipation: 220, travel: 200, contact: 150, 'follow-through': 150, hold: 140, settle: 190 },
    poses: {
      anticipation: { progress: 0, along: -9, side: 0, lift: 0, rotate: 0, scaleX: 1.12, scaleY: 0.76, shadow: 2 },
      travel: { progress: 0.82, along: 0, side: 0, lift: 8, rotate: 0, scaleX: 1.2, scaleY: 0.94, shadow: 9 },
      contact: { progress: 1, along: 5, side: 0, lift: -5, rotate: 0, scaleX: 1.27, scaleY: 0.72, shadow: 2 },
      'follow-through': { progress: 1, along: 11, side: 0, lift: -2, rotate: 0, scaleX: 1.16, scaleY: 0.83, shadow: 3 },
      hold: { progress: 1, along: 6, side: 0, lift: 0, rotate: 0, scaleX: 1.09, scaleY: 0.91, shadow: 2 },
      settle: REST_POSE,
    },
    targetRecoil: { distance: 39, side: 0, lift: 5, rotate: 5, scale: 0.74 },
    impact: { rotate: 0, scale: 1.72, drift: 4 },
  },
  q: {
    archetype: 'queen-fan-slash',
    impactMark: 'water-lily-petal-burst',
    accentClass: 'is-queen-fan-slash',
    reactionFamily: 'swept-petal-recoil',
    effectFamily: 's-curve-petals',
    effectLayers: [
      effectLayer('back', 'is-queen-petals-back', 0, 112, [{ opacity: 0, transform: 'translate(-28%,18%) rotate(-32deg) scale(0.42)' }, { opacity: 0.55, transform: 'translate(-12%,-8%) rotate(-8deg) scale(0.9)', offset: 0.4 }, { opacity: 0, transform: 'translate(10%,-34%) rotate(22deg) scale(1.22)' }]),
      effectLayer('ground', 'is-queen-petals-mid', 8, 110, [{ opacity: 0, transform: 'translate(18%,22%) rotate(28deg) scale(0.38)' }, { opacity: 0.78, transform: 'translate(2%,-4%) rotate(6deg) scale(1.04)', offset: 0.34 }, { opacity: 0, transform: 'translate(-22%,-30%) rotate(-26deg) scale(1.42)' }]),
      effectLayer('front', 'is-queen-petals-front', 16, 110, [{ opacity: 0, transform: 'translate(-12%,28%) rotate(-18deg) scale(0.3)' }, { opacity: 1, transform: 'translate(10%,-12%) rotate(16deg) scale(1.18)', offset: 0.28 }, { opacity: 0, transform: 'translate(34%,-38%) rotate(48deg) scale(1.62)' }]),
    ],
    timings: { anticipation: 180, travel: 250, contact: 130, 'follow-through': 190, hold: 180, settle: 170 },
    poses: {
      anticipation: { progress: 0, along: -8, side: 12, lift: 9, rotate: 11, scaleX: 0.92, scaleY: 0.97, shadow: 8 },
      travel: { progress: 0.7, along: 1, side: -16, lift: 29, rotate: -13, scaleX: 1.14, scaleY: 1.04, shadow: 17 },
      contact: { progress: 1, along: 8, side: 14, lift: 14, rotate: 17, scaleX: 1.2, scaleY: 1.03, shadow: 12 },
      'follow-through': { progress: 1, along: 18, side: -17, lift: 21, rotate: -18, scaleX: 1.1, scaleY: 1.06, shadow: 15 },
      hold: { progress: 1, along: 8, side: -7, lift: 11, rotate: -7, scaleX: 1.05, scaleY: 1.03, shadow: 10 },
      settle: REST_POSE,
    },
    targetRecoil: { distance: 27, side: -24, lift: 24, rotate: -24, scale: 0.76 },
    impact: { rotate: 56, scale: 1.68, drift: 19 },
  },
  k: {
    archetype: 'king-scepter-bonk',
    impactMark: 'defensive-ring-bonk',
    accentClass: 'is-king-scepter-bonk',
    reactionFamily: 'comic-bob-recoil',
    effectFamily: 'regal-ring-stars',
    effectLayers: [
      effectLayer('ground', 'is-king-ring', 10, 116, [{ opacity: 0, transform: 'translateY(12%) scale(0.42,0.28)' }, { opacity: 0.92, transform: 'translateY(8%) scale(1,0.62)', offset: 0.34 }, { opacity: 0, transform: 'translateY(4%) scale(1.46,0.88)' }]),
      effectLayer('front', 'is-king-stars', 22, 112, [{ opacity: 0, transform: 'translateY(12%) rotate(-18deg) scale(0.34)' }, { opacity: 1, transform: 'translateY(-12%) rotate(8deg) scale(1.08)', offset: 0.3 }, { opacity: 0, transform: 'translateY(-38%) rotate(38deg) scale(1.48)' }]),
    ],
    timings: { anticipation: 210, travel: 190, contact: 140, 'follow-through': 160, hold: 160, settle: 190 },
    poses: {
      anticipation: { progress: 0, along: -6, side: -3, lift: 1, rotate: -5, scaleX: 1.04, scaleY: 0.88, shadow: 3 },
      travel: { progress: 0.84, along: 0, side: 5, lift: 12, rotate: 7, scaleX: 1.09, scaleY: 1.01, shadow: 10 },
      contact: { progress: 1, along: 6, side: -4, lift: 2, rotate: -10, scaleX: 1.13, scaleY: 0.9, shadow: 4 },
      'follow-through': { progress: 1, along: 10, side: 5, lift: 7, rotate: 9, scaleX: 1.06, scaleY: 0.96, shadow: 7 },
      hold: { progress: 1, along: 5, side: 1, lift: 3, rotate: 2, scaleX: 1.03, scaleY: 0.98, shadow: 5 },
      settle: REST_POSE,
    },
    targetRecoil: { distance: 24, side: 7, lift: 8, rotate: 11, scale: 0.86 },
    impact: { rotate: -8, scale: 1.38, drift: 7 },
  },
} as const satisfies Readonly<Record<RulesPiece, CaptureActionSpec>>;

export interface CaptureActionGeometry {
  readonly source: ScreenCell;
  readonly target: ScreenCell;
  readonly destination: ScreenCell;
}

export interface CapturePhasePlan {
  readonly name: CapturePhaseName;
  readonly duration: number;
  readonly frame: 1 | 2 | 3 | 4 | 5;
  readonly frameBlendMs: number;
  readonly hitStopMs?: number;
  readonly moverKeyframes: Keyframe[];
  readonly shadowKeyframes: Keyframe[];
}

export interface CaptureActionPlan {
  readonly role: RulesPiece;
  readonly archetype: string;
  readonly impactMark: string;
  readonly accentClass: string;
  readonly reactionFamily: string;
  readonly effectFamily: string;
  readonly effectLayers: readonly CaptureEffectLayer[];
  readonly phases: readonly CapturePhasePlan[];
  readonly totalDuration: number;
  readonly direction: ScreenDirection;
  readonly travelDirection: ScreenDirection;
  readonly targetKeyframes: Readonly<Record<CapturePhaseName, Keyframe[]>>;
  readonly impactKeyframes: Keyframe[];
  readonly accentKeyframes: Keyframe[];
}

function directionBetween(source: ScreenCell, target: ScreenCell): ScreenDirection {
  const deltaX = target[0] - source[0];
  const deltaY = target[1] - source[1];
  const length = Math.hypot(deltaX, deltaY) || 1;
  const unitX = deltaX / length;
  const unitY = deltaY / length;
  return {
    deltaX,
    deltaY,
    unitX,
    unitY,
    perpendicularX: -unitY,
    perpendicularY: unitX,
  };
}

function compactPercent(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  return Object.is(rounded, -0) ? '0' : String(rounded);
}

function facing(direction: ScreenDirection): number {
  if (Math.abs(direction.unitX) > 0.01) return Math.sign(direction.unitX);
  return direction.unitY <= 0 ? 1 : -1;
}

function poseTransform(direction: ScreenDirection, pose: MotionPose): string {
  const x = (direction.deltaX * 100 * pose.progress)
    + (direction.unitX * pose.along)
    + (direction.perpendicularX * pose.side);
  const y = (direction.deltaY * 100 * pose.progress)
    + (direction.unitY * pose.along)
    + (direction.perpendicularY * pose.side)
    - pose.lift;
  const rotate = pose.rotate * facing(direction);
  return `translate(${compactPercent(x)}%,${compactPercent(y)}%) rotate(${compactPercent(rotate)}deg) scale(${pose.scaleX},${pose.scaleY})`;
}

function moverFrame(direction: ScreenDirection, pose: MotionPose): Keyframe {
  return {
    opacity: pose.opacity ?? 1,
    transform: poseTransform(direction, pose),
  };
}

function shadowFrame(direction: ScreenDirection, pose: MotionPose): Keyframe {
  const x = (direction.deltaX * 100 * pose.progress)
    + (direction.unitX * pose.along)
    + (direction.perpendicularX * pose.side);
  const y = (direction.deltaY * 100 * pose.progress)
    + (direction.unitY * pose.along)
    + (direction.perpendicularY * pose.side)
    + 28;
  const liftRatio = Math.min(1, Math.max(0, pose.lift) / 42);
  const spread = Math.max(0.58, 0.86 - (liftRatio * 0.24) + (pose.shadow * 0.004));
  const opacity = Math.max(0.12, 0.36 - (liftRatio * 0.2));
  return {
    opacity,
    transform: `translate(${compactPercent(x)}%,${compactPercent(y)}%) scale(${compactPercent(spread)},${compactPercent(spread * 0.42)})`,
  };
}

function targetTransform(direction: ScreenDirection, recoil: TargetRecoil, amount: number): string {
  const x = (direction.unitX * recoil.distance * amount)
    + (direction.perpendicularX * recoil.side * amount);
  const y = (direction.unitY * recoil.distance * amount)
    + (direction.perpendicularY * recoil.side * amount)
    - (recoil.lift * amount);
  return `translate(${compactPercent(x)}%,${compactPercent(y)}%) rotate(${compactPercent(recoil.rotate * facing(direction) * amount)}deg) scale(${compactPercent(1 - ((1 - recoil.scale) * amount))})`;
}

export function createCaptureActionPlan(
  role: RulesPiece,
  geometry: CaptureActionGeometry,
): CaptureActionPlan {
  const spec = CAPTURE_ACTION_REGISTRY[role];
  const direction = directionBetween(geometry.source, geometry.target);
  const travelDirection = directionBetween(geometry.source, geometry.destination);
  const destinationPose: MotionPose = { ...REST_POSE, progress: 1 };
  const phaseEnd = CAPTURE_PHASE_ORDER.reduce((frames, phase) => {
    frames[phase] = moverFrame(travelDirection, spec.poses[phase]);
    return frames;
  }, {} as Record<CapturePhaseName, Keyframe>);
  const shadowEnd = CAPTURE_PHASE_ORDER.reduce((frames, phase) => {
    frames[phase] = shadowFrame(travelDirection, spec.poses[phase]);
    return frames;
  }, {} as Record<CapturePhaseName, Keyframe>);
  const origin: MotionPose = { ...REST_POSE, progress: 0 };
  const destinationFrame = moverFrame(travelDirection, destinationPose);
  const originShadow = shadowFrame(travelDirection, origin);
  const destinationShadow = shadowFrame(travelDirection, destinationPose);
  const contactHitStopMs = 72;
  const contactBlendMs = 12;
  const contactPlateauOffset = (contactBlendMs + contactHitStopMs) / spec.timings.contact;
  const contactExitPose: MotionPose = {
    ...spec.poses.contact,
    along: spec.poses.contact.along + 4,
    lift: spec.poses.contact.lift + 2,
  };
  const contactExitFrame = moverFrame(travelDirection, contactExitPose);
  const contactExitShadow = shadowFrame(travelDirection, contactExitPose);
  const phases: CapturePhasePlan[] = [
    {
      name: 'anticipation',
      duration: spec.timings.anticipation,
      frame: 1,
      frameBlendMs: 28,
      moverKeyframes: [moverFrame(travelDirection, origin), phaseEnd.anticipation],
      shadowKeyframes: [originShadow, shadowEnd.anticipation],
    },
    {
      name: 'travel',
      duration: spec.timings.travel,
      frame: 2,
      frameBlendMs: 24,
      moverKeyframes: [phaseEnd.anticipation, { ...phaseEnd.travel, offset: 0.68 }, destinationFrame],
      shadowKeyframes: [shadowEnd.anticipation, { ...shadowEnd.travel, offset: 0.68 }, destinationShadow],
    },
    {
      name: 'contact',
      duration: spec.timings.contact,
      frame: 3,
      frameBlendMs: contactBlendMs,
      hitStopMs: contactHitStopMs,
      moverKeyframes: [
        destinationFrame,
        { ...phaseEnd.contact, offset: contactBlendMs / spec.timings.contact },
        { ...phaseEnd.contact, offset: contactPlateauOffset },
        contactExitFrame,
      ],
      shadowKeyframes: [
        destinationShadow,
        { ...shadowEnd.contact, offset: contactBlendMs / spec.timings.contact },
        { ...shadowEnd.contact, offset: contactPlateauOffset },
        contactExitShadow,
      ],
    },
    {
      name: 'follow-through',
      duration: spec.timings['follow-through'],
      frame: 4,
      frameBlendMs: 24,
      moverKeyframes: [contactExitFrame, { ...phaseEnd['follow-through'], offset: 0.7 }, phaseEnd.hold],
      shadowKeyframes: [contactExitShadow, { ...shadowEnd['follow-through'], offset: 0.7 }, shadowEnd.hold],
    },
    {
      name: 'hold',
      duration: spec.timings.hold,
      frame: 4,
      frameBlendMs: 0,
      moverKeyframes: [phaseEnd.hold, phaseEnd.hold],
      shadowKeyframes: [shadowEnd.hold, shadowEnd.hold],
    },
    {
      name: 'settle',
      duration: spec.timings.settle,
      frame: 5,
      frameBlendMs: 32,
      moverKeyframes: [phaseEnd.hold, { ...destinationFrame, offset: 0.72 }, destinationFrame],
      shadowKeyframes: [shadowEnd.hold, { ...destinationShadow, offset: 0.72 }, destinationShadow],
    },
  ];

  const stillTarget: Keyframe[] = [
    { opacity: 1, transform: 'translate(0%,0%) rotate(0deg) scale(1)' },
    { opacity: 1, transform: 'translate(0%,0%) rotate(0deg) scale(1)' },
  ];
  const recoilStart = targetTransform(direction, spec.targetRecoil, 0);
  const recoilContact = targetTransform(direction, spec.targetRecoil, 0.72);
  const recoilEnd = targetTransform(direction, spec.targetRecoil, 1);
  const targetKeyframes: Record<CapturePhaseName, Keyframe[]> = {
    anticipation: stillTarget,
    travel: stillTarget,
    contact: [
      { opacity: 1, transform: recoilStart },
      { opacity: 0.88, transform: recoilContact, offset: 0.46 },
      { opacity: 0.62, transform: recoilEnd },
    ],
    'follow-through': [
      { opacity: 0.62, transform: recoilEnd },
      { opacity: 0, transform: targetTransform(direction, spec.targetRecoil, 1.28) },
    ],
    hold: [
      { opacity: 0, transform: targetTransform(direction, spec.targetRecoil, 1.28) },
      { opacity: 0, transform: targetTransform(direction, spec.targetRecoil, 1.28) },
    ],
    settle: [
      { opacity: 0, transform: targetTransform(direction, spec.targetRecoil, 1.28) },
      { opacity: 0, transform: targetTransform(direction, spec.targetRecoil, 1.28) },
    ],
  };

  const impactX = direction.unitX * spec.impact.drift;
  const impactY = direction.unitY * spec.impact.drift;
  const impactRotation = spec.impact.rotate * facing(direction);

  return {
    role,
    archetype: spec.archetype,
    impactMark: spec.impactMark,
    accentClass: spec.accentClass,
    reactionFamily: spec.reactionFamily,
    effectFamily: spec.effectFamily,
    effectLayers: spec.effectLayers,
    phases,
    totalDuration: phases.reduce((total, phase) => total + phase.duration, 0),
    direction,
    travelDirection,
    targetKeyframes,
    impactKeyframes: [
      { opacity: 0, transform: `translate(0%,0%) rotate(0deg) scale(0.62)` },
      { opacity: 1, transform: `translate(${compactPercent(impactX)}%,${compactPercent(impactY)}%) rotate(${compactPercent(impactRotation)}deg) scale(${spec.impact.scale})`, offset: 0.34 },
      { opacity: 0, transform: `translate(${compactPercent(impactX * 1.5)}%,${compactPercent(impactY * 1.5)}%) rotate(${compactPercent(impactRotation * 1.3)}deg) scale(${compactPercent(spec.impact.scale * 1.16)})` },
    ],
    accentKeyframes: [
      { opacity: 0, transform: `translate(${compactPercent(-impactX * 0.4)}%,${compactPercent(-impactY * 0.4)}%) scale(0.72)` },
      { opacity: 0.92, transform: `translate(${compactPercent(impactX * 0.45)}%,${compactPercent(impactY * 0.45)}%) scale(${compactPercent(spec.impact.scale * 0.92)})`, offset: 0.28 },
      { opacity: 0, transform: `translate(${compactPercent(impactX)}%,${compactPercent(impactY)}%) scale(${compactPercent(spec.impact.scale * 1.08)})` },
    ],
  };
}
